import {
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  StackDivider,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { CiTimer, CiSearch } from "react-icons/ci";
import { useEffect, useState } from "react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  approveProposal,
  getProposals,
  updateProposalCustomStatus,
  rejectProposal,
} from "@/services/proposal.service";
import { Proposal, ProposalCustomStatus } from "@/models/proposal.model";
import { ProposalStatus } from "../ProposalTable/proposalStatusRenderer";
import PdfPreview from "../PdfPreview/PdfPreview";
import { cepFormatter } from "@/utils/cepFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { MdDescription } from "react-icons/md";
import { Form, Formik } from "formik";
import TextInputField from "../TextInputField";
import FilePreview from "../FilePreview";
import { formatPhone } from "@/utils/formatPhone";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";

export default function BankAnalysis() {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [visibleInput, setVisibleInput] = useState<string | undefined>(
    undefined
  );

  const handleSwitchChange = (cardId: string) => {
    setVisibleInput((prev) => (prev === cardId ? undefined : cardId));
  };

  const loadProposals = async () => {
    try {
      const { data } = await getProposals({
        page: 1,
        pageSize: 10,
        proposalId: "",
        statuses: [ProposalStatus.PENDING_BANK_ANALYSIS],
        name: search,
      });
      setProposals(data);
    } catch (error) {
      console.error("Error loading proposals:", error);
    }
  };

  const handleGetCustomStatus = (customStatus: string): string => {
    const customStatusParsed: ProposalCustomStatus = customStatus ? JSON.parse(customStatus) : ''
    const message = customStatusParsed.acoes?.analisar_proposta_banco?.mensagem
    return message ?? ""
  }

  const approveProsal = async (proposalId: string) => {
    try {
      await approveProposal(proposalId);
      toast({
        title: "Proposta aprovada",
        description: "A proposta foi aprovada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error approving proposal:", error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const cancelProposal = async (proposalId: string) => {
    try {
      await rejectProposal(proposalId);
      toast({
        title: "Proposta rejeitada",
        description: "A proposta foi rejeitada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error canceling proposal:", error);
      toast({
        title: "Erro",
        description: "Erro ao rejeitar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updateProposalStatus = async (proposalId: string, status: string) => {
    try {
      await updateProposalCustomStatus(proposalId, status);
      toast({
        title: "Proposta atualizada",
        description: "A proposta foi atualizada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error updating proposal status:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const validationSchema = Yup.object().shape({
    status: Yup.string()
      .min(3, "O status deve ter no mínimo 3 caracteres")
      .required("O status é obrigatório"),
  });

  useEffect(() => {
    loadProposals();
  }, [search]);

  function getPixTypeLabel(value?: string): string {
    switch (value) {
      case "EMAIL":
        return "Email";
      case "PHONE":
        return "Celular";
      case "DOCUMENT":
        return "CPF";
      default:
        return "Desconhecido";
    }
  }

  return (
    <Box w="100%" display="flex" flexDirection="column" p={{ base: 2, md: 4 }}>
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        w="100%"
      >
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiTimer size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Análise Banco
            </Text>
          </Flex>
        </Flex>
        <Flex mt={{ base: 4, md: 0 }} justifyContent="flex-start">
          <InputGroup width={{ base: "100%", md: "30%" }}>
            <InputLeftElement mt={5} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              mt={4}
              placeholder="Pesquisar..."
              variant="filled"
              height={{ base: "56px", md: "48px" }}
              type="text"
              bg="white"
              maxLength={30}
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              color="black"
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex wrap="wrap" w="100%" gap={4} mt={4}>
        {proposals.length === 0 && (
          <Center w="100%">
            <Heading mt={100} color="gray.600">
              Não há nada para analizar
            </Heading>
          </Center>
        )}
        {proposals.map((proposal, index) => (
          <Card key={index} w="100%">
            <CardBody display="flex" flexDirection="column" w="100%">
              <Stack divider={<StackDivider />} spacing="4" w="100%">
                <Box>
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    overflow="auto"
                  >
                    <Flex justify="center" w={{ base: "100%", md: "33%" }}>
                      <Box
                        p={{ base: 4, md: 4 }}
                        borderWidth="1px"
                        borderRadius="md"
                        boxShadow="base"
                        bg="white"
                        w={{ base: "100%", md: "300px" }}
                      >
                        <Flex align="center" mb={2}>
                          <Icon
                            as={MdDescription}
                            boxSize={{ base: 5, md: 5 }}
                            color="gray.500"
                            mr={2}
                          />
                          <Heading
                            size={{ base: "md", md: "md" }}
                            color="gray.700"
                            fontFamily="Lexend Deca"
                          >
                            Revisar Proposta
                          </Heading>
                        </Flex>
                        <VStack spacing={2} align="stretch">
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Número da Proposta
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.shortHash}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Nome
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.NaturalPerson?.name}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              CPF
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {cpfFormatter(
                                  proposal?.NaturalPerson?.document!
                                )}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              CEP
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {cepFormatter(
                                  proposal.NaturalPerson?.address?.zipCode
                                )}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Dados Bancários
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.BankDetail?.pixKeyType !==
                                "BANK_ACCOUNT"
                                  ? `Pix - ${getPixTypeLabel(proposal.BankDetail?.pixKeyType)}: ${proposal.BankDetail?.pixKey}`
                                  : `Agência ${proposal?.BankDetail?.agency}, Conta ${proposal?.BankDetail?.account}-${proposal?.BankDetail?.accountDigit}`}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Banco que vai bloquear o saldo
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal!.chosenFinancialInfo?.Product?.processor?.name}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Telefone
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {formatPhone(
                                  proposal!.NaturalPerson?.phoneNumber!
                                )}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Valor Liberado
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {BrlCurrencyFormatter(
                                  proposal!.chosenFinancialInfo?.releasedAmount
                                )}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Score Unico
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="sm"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal!.UnicoData?.unicoScore}
                              </Text>
                            </Box>
                          </Box>
                        </VStack>
                      </Box>
                    </Flex>
                    <Box w={{ base: "100%", md: "23%" }} h="60vh">
                      <FilePreview
                        fileUrl={proposal.UnicoData?.frontDocument!}
                      />
                    </Box>
                            <Box w={{ base: "100%", md: "23%" }} h="60vh">
                      <FilePreview
                        fileUrl={proposal.UnicoData?.backDocument!}
                      />
                    </Box>
                    <Box
                      ml={{ base: 0, md: "5%" }}
                      w={{ base: "100%", md: "25%" }}
                    >
                      <PdfPreview
                        pdfUrl={proposal.UnicoData?.evidenceSet!}
                        penultimatePage={false}
                      />
                    </Box>
                  </Flex>
                </Box>
                <Box>
                  <Flex
                    gap="2"
                    align="center"
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Flex gap="2" align="center">
                      <Button
                        variant="solid"
                        colorScheme="red"
                        onClick={() => cancelProposal(proposal.id!)}
                      >
                        Rejeitar
                      </Button>
                      <Button
                        variant="solid"
                        bg="#2b5b83"
                        onClick={() => approveProsal(proposal.id!)}
                      >
                        Aprovar
                      </Button>
                    </Flex>
                    <Flex
                      align="center"
                      gap="1"
                      w="100%"
                      flexDirection={{ base: "column", sm: "row" }}
                    >
                      <Flex align="center" gap="1" w="145px">
                        <FormLabel
                          htmlFor={`status-${proposal.id}`}
                          m={0}
                          w="107px"
                        >
                          Mudar Status
                        </FormLabel>
                        <Switch
                          id={`status-${proposal.id}`}
                          isChecked={visibleInput === proposal.id}
                          onChange={() => handleSwitchChange(proposal.id!)}
                        />
                      </Flex>
                      {visibleInput === proposal.id && (
                        <Formik
                          initialValues={{
                            status: handleGetCustomStatus(proposal?.customStatus!) ?? proposal.customStatus,
                          }}
                          validationSchema={validationSchema}
                          onSubmit={(values) =>
                            updateProposalStatus(proposal.id!, values.status)
                          }
                        >
                          {({ values, setFieldValue, errors, touched }) => (
                            <Form style={{ width: "100%" }}>
                              <Flex gap="2" align="center">
                                <TextInputField
                                  name="status"
                                  placeholder="Digite novo status da proposta"
                                  value={values.status}
                                  setFieldValue={setFieldValue}
                                  error={errors.status}
                                  touched={touched.status}
                                />
                                <Button
                                  type="submit"
                                  variant="solid"
                                  colorScheme="green"
                                >
                                  Salvar
                                </Button>
                              </Flex>
                            </Form>
                          )}
                        </Formik>
                      )}
                    </Flex>
                  </Flex>
                </Box>
              </Stack>
            </CardBody>
          </Card>
        ))}
      </Flex>
    </Box>
  );
}
